/**
 * DS_10 — "Complete Your Sleep Setup" cross-sell carousel (mobile <= 767px)
 * for the DCOP + OrthoCurve PDPs. Theme-native port of the won Intelligems
 * variant: the cards are server-rendered in snippets/ds10-cross-sell.liquid
 * (the won runtime fetch/scrape of 5 product pages is deliberately replaced),
 * so this class only reveals the module, runs Flickity, and keeps the card
 * list in sync with dismissals.
 *
 * - Dismissed products (added to cart from the sheet this session) are stored
 *   in sessionStorage as product handles and removed before Flickity init.
 * - Already-in-cart products never render (skipped server-side via cart.items).
 * - Tapping a card fires `ds10:openSheet` for Ds10Sheet (separate file, one
 *   class per file per ESLint); a successful sheet add fires `ds10:productAdded`
 *   back, which removes the card (Flickity remove + reloadCells + resize, as
 *   the won code did) and records the dismissal.
 * - Flickity comes from the global vendor bundle (vendor-scripts-v2.js, also
 *   deferred and loaded first). Init targets ONLY this module's container —
 *   never the PDP gallery (.product__main-photos).
 */

const MOBILE_MAX_WIDTH = 767;
const STORAGE_KEY = 'ds10HiddenCrossSellProducts';

class Ds10Carousel {
  constructor() {
    this.root = document.querySelector('[data-ds10-xsell]');
    this.flkty = null;
    if (this.root) {
      this.init();
    }
  }

  init() {
    // Mobile only — matches the won gate. The markup stays inline-hidden on
    // desktop and the (width >= 768px) CSS guard backstops it. clientWidth,
    // not innerWidth: it is what the CSS media query matches and is not
    // skewed by scrollbars or zoom.
    if (document.documentElement.clientWidth > MOBILE_MAX_WIDTH) {
      return;
    }

    this.readDismissed().forEach((handle) => {
      const card = this.root.querySelector(`[data-ds10-card="${handle}"]`);
      if (card) {
        card.remove();
      }
    });

    if (!this.root.querySelector('[data-ds10-card]')) {
      return; // nothing to show — module stays hidden
    }

    if (!window.Flickity) {
      console.error('[DS_10] Flickity global missing — cross-sell carousel not initialized');
      return;
    }

    this.root.style.removeProperty('display');
    this.flkty = new window.Flickity(this.root.querySelector('[data-ds10-cards]'), {
      cellAlign: 'left',
      contain: true,
      pageDots: true,
      prevNextButtons: false,
      draggable: true,
    });

    this.root.addEventListener('click', (event) => {
      const card = event.target.closest('[data-ds10-card]');
      if (!card) {
        return;
      }
      document.dispatchEvent(new CustomEvent('ds10:openSheet', {
        detail: { handle: card.getAttribute('data-ds10-card') },
      }));
    });

    document.addEventListener('ds10:productAdded', (event) => {
      this.dismiss(event.detail.handle);
    });
  }

  readDismissed() {
    try {
      const stored = JSON.parse(sessionStorage.getItem(STORAGE_KEY) || '[]');
      return Array.isArray(stored) ? stored : [];
    } catch (error) {
      return [];
    }
  }

  dismiss(handle) {
    const dismissed = this.readDismissed();
    if (!dismissed.includes(handle)) {
      dismissed.push(handle);
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(dismissed));
    }

    const card = this.root.querySelector(`[data-ds10-card="${handle}"]`);
    if (card && this.flkty) {
      this.flkty.remove(card);
      this.flkty.reloadCells();
      this.flkty.resize();
    }

    if (!this.root.querySelector('[data-ds10-card]')) {
      this.root.style.display = 'none';
    }
  }
}

document.addEventListener('DOMContentLoaded', () => {
  new Ds10Carousel(); // eslint-disable-line no-new
});
